"use client";

import {
  getCoursePlacementDraftApiV1AdminCoursesCourseIdPlacementGet,
  saveCoursePlacementDraftApiV1AdminCoursesCourseIdPlacementPut,
  approveReviewItemApiV1AdminReviewReviewTypeItemIdApprovePost,
} from "@/src/api/sdk.gen";
import { Link } from "@/src/lib/next-compat";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ClipboardList,
  GripVertical,
  Info,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";

import type { Level } from "@/lib/admin/course-details";
import { getDuplicateOptionIndices } from "@/lib/quiz-option-validation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Difficulty = "EASY" | "MEDIUM" | "HARD";

type DraftQuestion = {
  clientId: string;
  id?: number;
  questionText: string;
  options: string[];
  correctAnswer: string;
  difficulty: Difficulty;
  explanation: string;
  orderIndex: number;
};

type DraftLevel = {
  levelId: number;
  name: string;
  levelNumber: number;
  quizStatus: string | null;
  questions: DraftQuestion[];
};

type PlacementDraftResponse = {
  courseId: number;
  schemaVersion: string;
  levels: Array<{
    id: number;
    name: string;
    levelNumber: number;
    quiz: { status: string } | null;
    questions: Array<{
      id: number;
      questionText: string;
      options: string[];
      correctAnswer: string;
      difficulty: Difficulty;
      explanation: string | null;
      orderIndex: number;
    }>;
  }>;
  warnings: string[];
};

const fieldClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100";

const DIFFICULTY_STYLES: Record<Difficulty, { active: string; idle: string }> = {
  EASY: {
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    idle: "border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50",
  },
  MEDIUM: {
    active: "border-amber-300 bg-amber-50 text-amber-900",
    idle: "border-slate-200 bg-white text-slate-600 hover:border-amber-200 hover:bg-amber-50/50",
  },
  HARD: {
    active: "border-rose-300 bg-rose-50 text-rose-800",
    idle: "border-slate-200 bg-white text-slate-600 hover:border-rose-200 hover:bg-rose-50/50",
  },
};

function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `q-${Date.now()}-${Math.random()}`;
}

function emptyQuestion(orderIndex: number): DraftQuestion {
  return {
    clientId: newClientId(),
    questionText: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    difficulty: "MEDIUM",
    explanation: "",
    orderIndex,
  };
}

function mergeLevelsFromSources(
  catalog: Level[],
  apiLevels: PlacementDraftResponse["levels"],
  preserveQuestions?: DraftLevel[],
): DraftLevel[] {
  const catalogSorted =
    catalog.length > 0
      ? [...catalog].sort((a, b) => a.level_number - b.level_number)
      : [...apiLevels]
          .sort((a, b) => a.levelNumber - b.levelNumber)
          .map((l) => ({ id: l.id, name: l.name, level_number: l.levelNumber }));

  const apiById = new Map(apiLevels.map((l) => [l.id, l]));
  const prevById = new Map((preserveQuestions ?? []).map((l) => [l.levelId, l]));

  return catalogSorted.map((cat) => {
    const api = apiById.get(cat.id);
    const prev = prevById.get(cat.id);
    if (prev && prev.questions.length > 0 && !api) {
      return prev;
    }
    return {
      levelId: cat.id,
      name: cat.name,
      levelNumber: cat.level_number,
      quizStatus: api?.quiz?.status ?? prev?.quizStatus ?? null,
      questions:
        prev && prev.questions.length > 0
          ? prev.questions
          : (api?.questions.map((q) => ({
              clientId: newClientId(),
              id: q.id,
              questionText: q.questionText,
              options: q.options.length >= 2 ? q.options : ["", "", "", ""],
              correctAnswer: q.correctAnswer,
              difficulty: q.difficulty,
              explanation: q.explanation ?? "",
              orderIndex: q.orderIndex,
            })) ?? []),
    };
  });
}

function formatLevelName(name: string): string {
  if (!name) return name;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function aggregateStatus(levels: DraftLevel[]): string {
  const statuses = levels.map((l) => l.quizStatus).filter(Boolean) as string[];
  if (statuses.length === 0) return "DRAFT";
  if (statuses.includes("REJECTED")) return "REJECTED";
  if (statuses.includes("PENDING_REVIEW") || statuses.includes("UNDER_REVIEW")) {
    return "PENDING_REVIEW";
  }
  if (statuses.every((s) => s === "APPROVED")) return "APPROVED";
  return "PENDING_REVIEW";
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function countByDifficulty(questions: DraftQuestion[]): Record<Difficulty, number> {
  return {
    EASY: questions.filter((q) => q.difficulty === "EASY").length,
    MEDIUM: questions.filter((q) => q.difficulty === "MEDIUM").length,
    HARD: questions.filter((q) => q.difficulty === "HARD").length,
  };
}

function bankTargets(schemaVersion: string): {
  easy: number;
  medium: number;
  hard: number;
  label: string;
} {
  if (schemaVersion === "professional") {
    return { easy: 0, medium: 5, hard: 5, label: "10 medium/hard per level at quiz time" };
  }
  return { easy: 1, medium: 2, hard: 2, label: "1 easy, 2 medium, 2 hard per level at quiz time" };
}

function BankChip({
  label,
  current,
  target,
}: {
  label: string;
  current: number;
  target: number;
}) {
  if (target === 0) return null;
  const met = current >= target;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        met
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      {met && <Check className="h-3 w-3" />}
      {label} {current}/{target}
    </span>
  );
}

function McqQuestionEditor({
  question,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  index,
}: {
  question: DraftQuestion;
  onChange: (q: DraftQuestion) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  index: number;
}) {
  const correctIndex = question.options.findIndex(
    (o) => o.trim() === question.correctAnswer.trim(),
  );
  const duplicateOptionIndices = getDuplicateOptionIndices(question.options);
  const duplicateErrorId = `duplicate-options-${question.clientId}`;

  const setOption = (oi: number, value: string) => {
    const options = question.options.map((o, idx) => (idx === oi ? value : o));
    let correctAnswer = question.correctAnswer;
    if (correctIndex === oi) correctAnswer = value;
    onChange({ ...question, options, correctAnswer });
  };

  const optionLabels = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  return (
    <div className="group rounded-xl border border-slate-200 bg-white transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex flex-col gap-0.5 pt-1 opacity-40 group-hover:opacity-100">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
            aria-label="Move up"
          >
            <GripVertical className="h-4 w-4 rotate-90" />
          </button>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">Q{index + 1}</span>
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${DIFFICULTY_STYLES[question.difficulty].active}`}
            >
              {question.difficulty.toLowerCase()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          >
            Up
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-30"
          >
            Down
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label="Delete question"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap gap-2">
          {(["EASY", "MEDIUM", "HARD"] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange({ ...question, difficulty: d })}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                question.difficulty === d ? DIFFICULTY_STYLES[d].active : DIFFICULTY_STYLES[d].idle
              }`}
            >
              {d.toLowerCase()}
            </button>
          ))}
        </div>

        <textarea
          rows={2}
          value={question.questionText}
          onChange={(e) => onChange({ ...question, questionText: e.target.value })}
          className={fieldClass}
          placeholder="Write your question here…"
        />

        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500">Answer choices — click the circle to mark correct</p>
          {question.options.map((opt, oi) => {
            const isCorrect = correctIndex === oi;
            const isDuplicate = duplicateOptionIndices.has(oi);
            return (
              <div
                key={oi}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                  isDuplicate
                    ? "border-rose-400 bg-rose-50"
                    : isCorrect
                      ? "border-violet-300 bg-violet-50/60"
                      : "border-slate-200 bg-slate-50/50"
                }`}
              >
                <button
                  type="button"
                  onClick={() =>
                    onChange({ ...question, correctAnswer: question.options[oi] ?? "" })
                  }
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors ${
                    isCorrect
                      ? "border-violet-500 bg-violet-500 text-white"
                      : "border-slate-300 bg-white text-slate-500 hover:border-violet-300"
                  }`}
                  aria-label={`Mark option ${optionLabels[oi]} as correct`}
                >
                  {optionLabels[oi]}
                </button>
                <input
                  value={opt}
                  onChange={(e) => setOption(oi, e.target.value)}
                  className={`min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                    isDuplicate ? "text-rose-800" : "text-slate-900"
                  }`}
                  placeholder={`Option ${optionLabels[oi]}`}
                  aria-invalid={isDuplicate}
                  aria-describedby={isDuplicate ? duplicateErrorId : undefined}
                />
              </div>
            );
          })}
          {duplicateOptionIndices.size > 0 && (
            <p
              id={duplicateErrorId}
              className="flex items-center gap-1.5 text-xs font-medium text-rose-700"
              role="alert"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              All answer choices must be different.
            </p>
          )}
        </div>

        <details className="rounded-lg border border-slate-100 bg-slate-50/50">
          <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-slate-500">
            Optional explanation
          </summary>
          <div className="border-t border-slate-100 p-3">
            <textarea
              rows={2}
              value={question.explanation}
              onChange={(e) => onChange({ ...question, explanation: e.target.value })}
              className={fieldClass}
              placeholder="Why is this the right answer?"
            />
          </div>
        </details>
      </div>
    </div>
  );
}

function GenerateWithAiButton({
  onGenerate,
  disabledReason,
  variant = "header",
}: {
  onGenerate: () => void;
  disabledReason: string | null;
  variant?: "header" | "empty";
}) {
  const disabled = disabledReason != null;
  const enabledClass =
    variant === "header"
      ? "inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3.5 py-2 text-sm font-semibold text-violet-700 shadow-sm transition-colors hover:border-violet-400 hover:bg-violet-50"
      : "inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-4 py-2 text-sm font-semibold text-violet-700 shadow-sm transition-colors hover:border-violet-400 hover:bg-violet-50";
  const disabledClass =
    variant === "header"
      ? "inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-semibold text-slate-400 shadow-sm cursor-not-allowed"
      : "inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-400 shadow-sm cursor-not-allowed";

  const button = (
    <button
      type="button"
      onClick={onGenerate}
      disabled={disabled}
      aria-disabled={disabled}
      className={disabled ? disabledClass : enabledClass}
    >
      <Sparkles className={`h-4 w-4 ${disabled ? "text-slate-300" : "text-violet-500"}`} />
      Generate with AI
    </button>
  );

  if (!disabled) return button;

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-not-allowed" tabIndex={0} aria-label={disabledReason ?? undefined}>
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align={variant === "header" ? "end" : "center"}
        sideOffset={8}
        className="z-50 max-w-[15rem] overflow-hidden rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium leading-relaxed text-slate-600 shadow-lg shadow-slate-200/60 animate-in fade-in-0 zoom-in-95"
      >
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

export default function PlacementTab({
  courseId,
  courseLevels,
  generatePlacementQuizDisabledReason = null,
  onGeneratePlacementQuiz,
  reloadNonce = 0,
}: {
  courseId: number;
  courseUuid: string;
  courseLevels: Level[];
  generatePlacementQuizDisabledReason?: string | null;
  onGeneratePlacementQuiz?: () => void;
  reloadNonce?: number;
}) {
  const [levels, setLevels] = useState<DraftLevel[]>([]);
  const [activeLevelId, setActiveLevelId] = useState<number | null>(null);
  const [schemaVersion, setSchemaVersion] = useState("engineering");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resData, error: reqErr } = await getCoursePlacementDraftApiV1AdminCoursesCourseIdPlacementGet({
        path: { course_id: courseId as any },
      });
      if (reqErr || !resData) {
        const body = (reqErr || {}) as { error?: string };
        throw new Error(body.error || "Failed to load placement");
      }
      const draft = resData as unknown as PlacementDraftResponse;
      setSchemaVersion(draft.schemaVersion);
      const mapped = mergeLevelsFromSources(courseLevels, draft.levels);
      setLevels(mapped);
      setActiveLevelId((prev) => {
        if (prev != null && mapped.some((l) => l.levelId === prev)) return prev;
        return mapped[0]?.levelId ?? null;
      });
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load placement");
    } finally {
      setLoading(false);
    }
  }, [courseId, courseLevels]);

  useEffect(() => {
    void load();
  }, [load, reloadNonce]);

  // Keep tabs in sync when course levels change (e.g. new level added in builder).
  useEffect(() => {
    if (courseLevels.length === 0 || loading) return;
    setLevels((prev) => mergeLevelsFromSources(courseLevels, [], prev));
    setActiveLevelId((current) => {
      if (current != null && courseLevels.some((l) => l.id === current)) return current;
      const sorted = [...courseLevels].sort((a, b) => a.level_number - b.level_number);
      return sorted[0]?.id ?? null;
    });
  }, [courseLevels, loading]);

  const activeLevel = useMemo(
    () => levels.find((l) => l.levelId === activeLevelId) ?? null,
    [levels, activeLevelId],
  );

  const targets = bankTargets(schemaVersion);
  const displayStatus = aggregateStatus(levels);
  const totalQuestions = levels.reduce((n, l) => n + l.questions.length, 0);
  const hasDuplicateOptions = levels.some((level) =>
    level.questions.some((question) => getDuplicateOptionIndices(question.options).size > 0),
  );

  const updateLevel = (levelId: number, patch: Partial<DraftLevel>) => {
    setDirty(true);
    setSuccess(null);
    setLevels((prev) => prev.map((l) => (l.levelId === levelId ? { ...l, ...patch } : l)));
  };

  const buildPayload = () => ({
    levels: levels.map((l) => ({
      levelId: l.levelId,
      questions: l.questions.map((q, i) => ({
        ...(q.id ? { id: q.id } : {}),
        questionText: q.questionText.trim(),
        options: q.options.map((o) => o.trim()).filter(Boolean),
        correctAnswer: q.correctAnswer.trim(),
        difficulty: q.difficulty,
        ...(q.explanation.trim() ? { explanation: q.explanation.trim() } : {}),
        orderIndex: i,
      })),
    })),
  });

  const handleSave = async () => {
    if (hasDuplicateOptions) {
      setError("All answer choices must be different. Fix the highlighted options before saving.");
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const hadApprovedBeforeSave = levels.some((l) => l.quizStatus === "APPROVED");
    try {
      const { data, error: reqErr } = await saveCoursePlacementDraftApiV1AdminCoursesCourseIdPlacementPut({
        path: { course_id: courseId as any },
        body: buildPayload() as any,
      });
      const body = (data || reqErr || {}) as {
        error?: string;
        details?: Record<string, string[]>;
      };
      if (reqErr) {
        const detail = body.details
          ? Object.entries(body.details)
              .map(([k, v]) => `${k}: ${v.join(", ")}`)
              .join("; ")
          : "";
        throw new Error(body.error ? `${body.error}${detail ? ` (${detail})` : ""}` : "Save failed");
      }
      setSuccess(
        hadApprovedBeforeSave
          ? "Saved successfully. Note: changes to an approved placement quiz move it back into review and can temporarily hide it from students until it’s approved again."
          : "Saved successfully.",
      );
      setDirty(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (hasDuplicateOptions) {
      setError("All answer choices must be different. Fix the highlighted options before approving.");
      return;
    }
    const missing = levels.filter((l) => l.questions.length === 0);
    if (missing.length > 0) {
      setError(`Add at least one question for every level before approving.`);
      return;
    }
    if (dirty) {
      setError("Save your changes before approving.");
      return;
    }

    setApproving(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: reqErr } = await approveReviewItemApiV1AdminReviewReviewTypeItemIdApprovePost({
        path: { review_type: "course_placement", item_id: String(courseId) },
      });
      if (reqErr) {
        const body = (reqErr || {}) as { error?: string };
        throw new Error(body.error || "Approve failed");
      }
      setSuccess("Placement quiz approved.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-16 text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
        Loading placement questions…
      </div>
    );
  }

  if (levels.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-8 py-14 text-center shadow-sm">
        <ClipboardList className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-4 font-medium text-slate-800">No levels in this course yet</p>
        <p className="mt-1 text-sm text-slate-500">
          Add levels (e.g. Associate, Intermediate, Advanced) in the course builder — you&apos;ll get one
          placement tab per level.
        </p>
        <Link
          to={`/admin/courses/builder?course=${courseId}`}
          className="mt-5 inline-flex items-center rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
        >
          Open course builder
        </Link>
      </div>
    );
  }

  const levelCounts = activeLevel ? countByDifficulty(activeLevel.questions) : null;

  return (
    <TooltipProvider>
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-violet-50/40 px-6 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">Placement quiz</h2>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  displayStatus === "APPROVED"
                    ? "bg-emerald-100 text-emerald-800"
                    : displayStatus === "REJECTED"
                      ? "bg-rose-100 text-rose-800"
                      : "bg-slate-200 text-slate-700"
                }`}
              >
                {statusLabel(displayStatus)}
              </span>
              {dirty && (
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                  Unsaved changes
                </span>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">
              {totalQuestions} question{totalQuestions === 1 ? "" : "s"} across {levels.length} level
              {levels.length === 1 ? "" : "s"}. Students see a random subset ({targets.label}).
              {onGeneratePlacementQuiz && !generatePlacementQuizDisabledReason
                ? " Add questions manually or generate them with AI."
                : " Add and edit questions per level."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            {onGeneratePlacementQuiz && (
              <GenerateWithAiButton
                onGenerate={onGeneratePlacementQuiz}
                disabledReason={generatePlacementQuizDisabledReason}
              />
            )}
            <button
              type="button"
              onClick={() => void handleApprove()}
              disabled={approving || saving || displayStatus === "APPROVED"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {approving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              )}
              Approve
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || approving || !dirty}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(error || success) && (
        <div className="border-b border-slate-100 px-6 py-3">
          {error && (
            <div className="flex items-start gap-2 text-sm text-rose-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {success && !error && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <Check className="h-4 w-4" />
              {success}
            </div>
          )}
        </div>
      )}

      {/* Level tabs — one per course level */}
      <div className="border-b border-slate-200 bg-slate-50/60 px-4 pt-3">
        <div className="flex gap-0 overflow-x-auto" role="tablist" aria-label="Course levels">
          {levels.map((level) => {
            const active = level.levelId === activeLevelId;
            const count = level.questions.length;
            return (
              <button
                key={level.levelId}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveLevelId(level.levelId)}
                className={`shrink-0 rounded-t-lg border border-b-0 px-5 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? "relative z-10 -mb-px border-slate-200 border-b-white bg-white text-violet-700 shadow-sm"
                    : "border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-white/80 hover:text-slate-900"
                }`}
              >
                {formatLevelName(level.name)}
                <span
                  className={`ml-2 inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                    active ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active level workspace */}
      {activeLevel && (
        <div className="p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Level {activeLevel.levelNumber} · {formatLevelName(activeLevel.name)}
              </h3>
              {activeLevel.quizStatus && (
                <p className="text-xs text-slate-500">{statusLabel(activeLevel.quizStatus)}</p>
              )}
            </div>
            {levelCounts && (
              <div className="flex flex-wrap items-center gap-2">
                <BankChip label="Easy" current={levelCounts.EASY} target={targets.easy} />
                <BankChip label="Med" current={levelCounts.MEDIUM} target={targets.medium} />
                <BankChip label="Hard" current={levelCounts.HARD} target={targets.hard} />
              </div>
            )}
          </div>

          {schemaVersion === "professional" && (
            <div className="mb-5 flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              Professional courses only use medium and hard questions in the placement quiz.
            </div>
          )}

          {activeLevel.questions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-6 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100">
                <ClipboardList className="h-6 w-6 text-violet-600" />
              </div>
              <p className="mt-4 font-semibold text-slate-800">No questions for this level yet</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
                Start with a manual question, or generate the full quiz with AI when available.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    updateLevel(activeLevel.levelId, {
                      questions: [emptyQuestion(0)],
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700"
                >
                  <Plus className="h-4 w-4" />
                  Add first question
                </button>
                {onGeneratePlacementQuiz && (
                  <GenerateWithAiButton
                    onGenerate={onGeneratePlacementQuiz}
                    disabledReason={generatePlacementQuizDisabledReason}
                    variant="empty"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeLevel.questions.map((q, qi) => (
                <McqQuestionEditor
                  key={q.clientId}
                  question={q}
                  index={qi}
                  canMoveUp={qi > 0}
                  canMoveDown={qi < activeLevel.questions.length - 1}
                  onChange={(next) => {
                    const questions = [...activeLevel.questions];
                    questions[qi] = next;
                    updateLevel(activeLevel.levelId, { questions });
                  }}
                  onDelete={() => {
                    updateLevel(activeLevel.levelId, {
                      questions: activeLevel.questions.filter((_, i) => i !== qi),
                    });
                  }}
                  onMoveUp={() => {
                    if (qi === 0) return;
                    const questions = [...activeLevel.questions];
                    [questions[qi - 1], questions[qi]] = [questions[qi], questions[qi - 1]];
                    updateLevel(activeLevel.levelId, { questions });
                  }}
                  onMoveDown={() => {
                    if (qi >= activeLevel.questions.length - 1) return;
                    const questions = [...activeLevel.questions];
                    [questions[qi], questions[qi + 1]] = [questions[qi + 1], questions[qi]];
                    updateLevel(activeLevel.levelId, { questions });
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() =>
                  updateLevel(activeLevel.levelId, {
                    questions: [
                      ...activeLevel.questions,
                      emptyQuestion(activeLevel.questions.length),
                    ],
                  })
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50/50 hover:text-violet-700"
              >
                <Plus className="h-4 w-4" />
                Add another question
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
